export type Vec3 = [number, number, number];
export interface RigTransform {
  position: Vec3;
  rotation: Vec3;
  scale: Vec3;
}
export interface RigJoint {
  name: string;
  parent?: string | null;
  bind?: Partial<RigTransform>;
  /** false = control joint (IK target / pole / socket): never auto-weighted. */
  deform?: boolean;
}
export type KeyInterp = 'constant' | 'linear' | 'bezier';
export type TangentMode = 'auto' | 'aligned' | 'free' | 'linear' | 'constant';
/** Bezier handles of one channel at a key: offsets (seconds, value) from the key. */
export interface RigTangent {
  mode?: TangentMode;
  in?: [number, number];
  out?: [number, number];
}
export interface RigKey {
  time: number;
  transform: Partial<RigTransform>;
  /** Interpolation of the segment starting at this key (default linear). */
  interpolation?: KeyInterp;
  /** Per channel ("position.x" … "scale.z"); missing = auto. */
  tangents?: Record<string, RigTangent>;
}
export interface RigTrack {
  joint: string;
  keys: RigKey[];
}
export interface RigLayer {
  name: string;
  blend?: 'override' | 'additive';
  weight?: number;
  /** Joint subtrees; "!Name" excludes (Animator layer mask syntax). */
  mask?: string[];
  mute?: boolean;
  tracks: RigTrack[];
}
export interface RigClip {
  name: string;
  tracks: RigTrack[];
  layers?: RigLayer[];
  fps?: number;
}
/** A joint name or a model-space point. */
export type ConstraintTarget = string | Vec3;
interface ConstraintBase {
  name?: string;
  bone: string;
  influence?: number;
  enabled?: boolean;
}
export type AxisDir = 'x' | 'y' | 'z' | '-x' | '-y' | '-z';
export type RigConstraint =
  | (ConstraintBase & { type: 'twoBoneIk'; target: ConstraintTarget; pole?: ConstraintTarget; targetRotation?: boolean })
  | (ConstraintBase & { type: 'chainIk'; chainLength: number; target: ConstraintTarget; pole?: ConstraintTarget; iterations?: number })
  | (ConstraintBase & { type: 'copyRotation'; target: string; space?: 'world' | 'local'; axes?: [boolean, boolean, boolean]; mixAdd?: boolean })
  | (ConstraintBase & { type: 'copyLocation'; target: ConstraintTarget; space?: 'world' | 'local'; axes?: [boolean, boolean, boolean]; offset?: boolean })
  | (ConstraintBase & { type: 'limitRotation'; min?: [number | null, number | null, number | null]; max?: [number | null, number | null, number | null] })
  | (ConstraintBase & { type: 'dampedTrack'; target: ConstraintTarget; axis?: AxisDir })
  | (ConstraintBase & { type: 'lookAt'; target: ConstraintTarget; aim?: AxisDir; up?: AxisDir })
  | (ConstraintBase & { type: 'childOf'; target: string });
export type ConstraintType = RigConstraint['type'];
export interface Influence {
  joint: string;
  weight: number;
}
export interface RigDoc {
  replaceSkin?: boolean;
  joints: RigJoint[];
  clips?: RigClip[];
  rigidBindings?: Record<string, string>;
  weights?: Record<string, Influence[][][]>;
  /** Bone constraints evaluated in order after animation (editor + runtime). */
  constraints?: RigConstraint[];
}
export interface RigFrame {
  positions: Vec3[][];
  joints: {
    name: string;
    parent: string | null;
    position: Vec3;
    local: RigTransform;
    global: number[];
  }[];
}
export interface RigGeometry {
  nodeNames: string[];
  sourceSkinned: boolean;
  meshes: {
    node: string;
    primitive: number;
    indices: number[];
    color: number[];
    joints: number[][];
    weights: number[][];
  }[];
  frame: RigFrame;
  clips: { name: string; duration: number }[];
}

export function transform(t?: Partial<RigTransform>): RigTransform {
  return {
    position: [...(t?.position ?? [0, 0, 0])],
    rotation: [...(t?.rotation ?? [0, 0, 0])],
    scale: [...(t?.scale ?? [1, 1, 1])],
  };
}
export function cloneRig(rig: RigDoc): RigDoc {
  return structuredClone(rig);
}
export function uniqueName(base: string, names: string[]): string {
  if (!names.includes(base)) return base;
  let i = 2;
  while (names.includes(`${base} ${i}`)) i++;
  return `${base} ${i}`;
}
export function descendants(rig: RigDoc, name: string): Set<string> {
  const result = new Set([name]);
  for (let i = 0; i < rig.joints.length; i++)
    for (const joint of rig.joints)
      if (joint.parent && result.has(joint.parent)) result.add(joint.name);
  return result;
}
export function renameJoint(rig: RigDoc, from: string, to: string): RigDoc {
  if (!to.trim() || rig.joints.some((j) => j.name === to && j.name !== from))
    throw new Error('Joint names must be unique and nonempty.');
  const next = cloneRig(rig);
  for (const joint of next.joints) {
    if (joint.name === from) joint.name = to;
    if (joint.parent === from) joint.parent = to;
  }
  for (const clip of next.clips ?? [])
    for (const track of [...clip.tracks, ...(clip.layers ?? []).flatMap((l) => l.tracks)])
      if (track.joint === from) track.joint = to;
  for (const c of next.constraints ?? []) {
    const r = c as unknown as Record<string, unknown>;
    for (const key of ['bone', 'target', 'pole'] as const) if (r[key] === from) r[key] = to;
  }
  for (const [node, joint] of Object.entries(next.rigidBindings ?? {}))
    if (joint === from) next.rigidBindings![node] = to;
  for (const primitives of Object.values(next.weights ?? {}))
    for (const vertices of primitives)
      for (const weights of vertices)
        for (const influence of weights) if (influence.joint === from) influence.joint = to;
  return next;
}
export function removeJoint(rig: RigDoc, name: string): RigDoc {
  const next = cloneRig(rig),
    removed = descendants(rig, name);
  next.joints = next.joints.filter((j) => !removed.has(j.name));
  for (const clip of next.clips ?? []) {
    clip.tracks = clip.tracks.filter((t) => !removed.has(t.joint));
    for (const layer of clip.layers ?? []) layer.tracks = layer.tracks.filter((t) => !removed.has(t.joint));
  }
  next.clips = next.clips?.filter((c) => c.tracks.length > 0 || (c.layers ?? []).some((l) => l.tracks.length));
  if (next.constraints)
    next.constraints = next.constraints.filter((c) => {
      const r = c as unknown as Record<string, unknown>;
      return !(['bone', 'target', 'pole'] as const).some((k) => typeof r[k] === 'string' && removed.has(r[k] as string));
    });
  for (const [node, joint] of Object.entries(next.rigidBindings ?? {}))
    if (removed.has(joint)) delete next.rigidBindings![node];
  // Rebind affected nodes rather than retaining dangling/zero-sum influences.
  for (const [node, primitives] of Object.entries(next.weights ?? {}))
    if (primitives.some((p) => p.some((v) => v.some((w) => removed.has(w.joint)))))
      delete next.weights![node];
  return next;
}
export function duration(clip?: RigClip): number {
  const tracks = [...(clip?.tracks ?? []), ...(clip?.layers ?? []).flatMap((l) => l.tracks)];
  return Math.max(0, ...tracks.flatMap((t) => t.keys.map((k) => k.time)));
}
/** Tracks of the base clip (layer -1) or of one of its layers. */
export function layerTracks(clip: RigClip, layer: number): RigTrack[] | null {
  if (layer < 0) return clip.tracks;
  return clip.layers?.[layer]?.tracks ?? null;
}
export function setKey(
  rig: RigDoc,
  clip: string,
  joint: string,
  time: number,
  pose: RigTransform,
  layer = -1,
  interpolation?: KeyInterp,
): RigDoc {
  const next = cloneRig(rig),
    target = next.clips?.find((c) => c.name === clip);
  if (!target) return rig;
  const tracks = layerTracks(target, layer);
  if (!tracks) return rig;
  let track = tracks.find((t) => t.joint === joint);
  if (!track) {
    track = { joint, keys: [] };
    tracks.push(track);
  }
  const key = track.keys.find((k) => Math.abs(k.time - time) < 1e-5);
  if (key) key.transform = transform(pose);
  else {
    // New keys inherit the interpolation of the key before them.
    const before = [...track.keys].reverse().find((k) => k.time < time);
    const interp = interpolation ?? before?.interpolation;
    track.keys.push({ time, transform: transform(pose), ...(interp && interp !== 'linear' ? { interpolation: interp } : {}) });
  }
  track.keys.sort((a, b) => a.time - b.time);
  return next;
}
export function normalizeWeights(weights: Influence[]): Influence[] {
  const totals = new Map<string, number>();
  for (const w of weights)
    if (Number.isFinite(w.weight) && w.weight > 0)
      totals.set(w.joint, (totals.get(w.joint) ?? 0) + w.weight);
  const result = [...totals].sort((a, b) => b[1] - a[1]).slice(0, 4);
  const sum = result.reduce((s, w) => s + w[1], 0);
  return result.map(([joint, weight]) => ({ joint, weight: weight / sum }));
}
export function matrix(t: RigTransform): number[] {
  const [x, y, z] = t.rotation.map((v) => (v * Math.PI) / 180) as Vec3;
  const [cx, sx, cy, sy, cz, sz] = [
    Math.cos(x),
    Math.sin(x),
    Math.cos(y),
    Math.sin(y),
    Math.cos(z),
    Math.sin(z),
  ];
  return [
    cy * cz * t.scale[0],
    (cx * sz + sx * sy * cz) * t.scale[0],
    (sx * sz - cx * sy * cz) * t.scale[0],
    0,
    -cy * sz * t.scale[1],
    (cx * cz - sx * sy * sz) * t.scale[1],
    (sx * cz + cx * sy * sz) * t.scale[1],
    0,
    sy * t.scale[2],
    -sx * cy * t.scale[2],
    cx * cy * t.scale[2],
    0,
    ...t.position,
    1,
  ];
}
export function multiply(a: number[], b: number[]): number[] {
  return Array.from({ length: 16 }, (_, i) => {
    const row = i % 4,
      col = Math.floor(i / 4);
    return [0, 1, 2, 3].reduce((s, k) => s + a[k * 4 + row]! * b[col * 4 + k]!, 0);
  });
}
export function inverse(m: number[]): number[] {
  const rows = Array.from({ length: 4 }, (_, r) => [
    ...Array.from({ length: 4 }, (_, c) => m[c * 4 + r]!),
    ...Array.from({ length: 4 }, (_, c) => Number(r === c)),
  ]);
  for (let c = 0; c < 4; c++) {
    let pivot = c;
    for (let r = c + 1; r < 4; r++)
      if (Math.abs(rows[r]![c]!) > Math.abs(rows[pivot]![c]!)) pivot = r;
    [rows[c], rows[pivot]] = [rows[pivot]!, rows[c]!];
    const d = rows[c]![c]!;
    if (Math.abs(d) < 1e-10) throw new Error('Transform has zero scale.');
    rows[c] = rows[c]!.map((v) => v / d);
    for (let r = 0; r < 4; r++)
      if (r !== c) {
        const factor = rows[r]![c]!;
        rows[r] = rows[r]!.map((v, k) => v - factor * rows[c]![k]!);
      }
  }
  return Array.from({ length: 16 }, (_, i) => rows[i % 4]![4 + Math.floor(i / 4)]!);
}
export function point(m: number[], p: Vec3, vector = false): Vec3 {
  return [0, 1, 2].map(
    (r) => m[r]! * p[0] + m[4 + r]! * p[1] + m[8 + r]! * p[2] + (vector ? 0 : m[12 + r]!),
  ) as Vec3;
}
export function decompose(m: number[]): RigTransform {
  const scale = [0, 1, 2].map((c) => Math.hypot(m[c * 4]!, m[c * 4 + 1]!, m[c * 4 + 2]!)) as Vec3;
  const y = Math.asin(Math.max(-1, Math.min(1, m[8]! / scale[2])));
  const rotation: Vec3 =
    Math.abs(Math.cos(y)) > 1e-6
      ? [
          Math.atan2(-m[9]! / scale[2], m[10]! / scale[2]),
          y,
          Math.atan2(-m[4]! / scale[1], m[0]! / scale[0]),
        ]
      : [Math.atan2(m[6]! / scale[1], m[5]! / scale[1]), y, 0];
  return {
    position: [m[12]!, m[13]!, m[14]!],
    rotation: rotation.map((v) => (v * 180) / Math.PI) as Vec3,
    scale,
  };
}
export function localBind(rig: RigDoc, joint: RigJoint): RigTransform {
  const parent = rig.joints.find((j) => j.name === joint.parent);
  return parent
    ? decompose(multiply(inverse(matrix(transform(parent.bind))), matrix(transform(joint.bind))))
    : transform(joint.bind);
}
export function setJointBind(
  rig: RigDoc,
  name: string,
  bind: RigTransform,
  children = true,
): RigDoc {
  const next = cloneRig(rig),
    joint = next.joints.find((j) => j.name === name);
  if (!joint) return rig;
  const change = multiply(matrix(bind), inverse(matrix(transform(joint.bind))));
  if (children) {
    const subtree = descendants(rig, name);
    for (const child of next.joints)
      if (child.name !== name && subtree.has(child.name))
        child.bind = decompose(multiply(change, matrix(transform(child.bind))));
  }
  joint.bind = bind;
  return next;
}
